import { useState, useEffect, useRef } from 'react';
import { Sparkles } from 'lucide-react';

interface Props {
  messages: string[];
  estimatedSeconds?: number;
}

export function AIProgressLoader({ messages, estimatedSeconds = 30 }: Props) {
  const [progress, setProgress] = useState(0);
  const [messageIndex, setMessageIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const startTime = useRef(Date.now());

  // Asymptotic progress curve — fast start, decelerates, caps at ~92%
  useEffect(() => {
    const tick = setInterval(() => {
      const elapsed = (Date.now() - startTime.current) / 1000;
      const p = Math.min(92, 100 * (1 - Math.exp(-2.5 * (elapsed / estimatedSeconds))));
      setProgress(p);
    }, 150);
    return () => clearInterval(tick);
  }, [estimatedSeconds]);

  // Cycle status messages with crossfade
  useEffect(() => {
    if (messages.length <= 1) return;
    const cycle = setInterval(() => {
      setFading(true);
      setTimeout(() => {
        setMessageIndex(i => i + 1 < messages.length ? i + 1 : i);
        setFading(false);
      }, 250);
    }, 3200);
    return () => clearInterval(cycle);
  }, [messages.length]);

  return (
    <div className="flex flex-col items-center gap-4 w-full max-w-xs mx-auto">
      <div className="w-10 h-10 rounded-xl bg-chart-teal/10 flex items-center justify-center">
        <Sparkles size={20} className="text-chart-teal animate-pulse-soft" />
      </div>

      <div className="w-full">
        <div
          className="w-full h-1.5 rounded-full overflow-hidden"
          style={{ backgroundColor: 'var(--color-surface)' }}
        >
          <div
            className="h-full rounded-full"
            style={{
              width: `${progress}%`,
              background: 'linear-gradient(90deg, #0D9488, #0891B2)',
              transition: 'width 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
            }}
          />
        </div>
      </div>

      <p
        className="text-sm text-secondary text-center min-h-[1.25rem]"
        style={{ transition: 'opacity 0.25s ease', opacity: fading ? 0 : 1 }}
      >
        {messages[messageIndex]}
      </p>
    </div>
  );
}
